import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const CanadaPost = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [formData, setFormData] = useState({
    cp_app_start: "",
    cp_comp_received: "",
    cp_certificate_number: "",
    cp_photo_id_pending: false,
    cp_photo_id_yes: false,
    cp_photo_id_no: false,
    cp_photo_id_code: "",
  });
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSigned, setIsSigned] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchCanadaPost = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/canadapost/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      const data = res.data;
      setFormData({
        cp_app_start: data.cp_app_start || "",
        cp_comp_received: data.cp_comp_received || "",
        cp_certificate_number: data.cp_certificate_number || "",
        cp_photo_id_pending: !!data.cp_photo_id_pending,
        cp_photo_id_yes: !!data.cp_photo_id_yes,
        cp_photo_id_no: !!data.cp_photo_id_no,
        cp_photo_id_code: data.cp_photo_id_code || "",
      });

      setUploadedFiles(data.files || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchCanadaPost();
  }, [fetchCanadaPost]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const TASK_ID = 4; // verify this matches nwt_tasks.t_id for LEEP
        const idKey = String(TASK_ID);
        const nameKey = "canada_post";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Task check error:", err);
      }
    };

    fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (allowedTypes.includes(selected.type)) {
      setFile(selected);
    } else {
      alert("Only PDF, JPG, or PNG files are allowed!");
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return alert("Please select a PDF file to upload.");

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));
    data.append("userId", userId);
    data.append("file", file);

    try {
      await axios.post(
        `${API_BASE}/api/canadapost/upload?userId=${userId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert("File uploaded!");
      setFile(null);
      fetchCanadaPost();
    } catch (error) {
      console.error("Upload error:", error);
      alert("Upload failed. Try again.");
    }
  };

  const handleDelete = async (filePath) => {
    try {
      await axios.delete(`${API_BASE}/api/canadapost/file`, {
        data: { filePath, userId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      alert("File deleted");
      fetchCanadaPost();
    } catch (error) {
      console.error("Delete error:", error);
      alert("File deletion failed.");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Canada Post Clearance
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Canada Post Clearance</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit} className="mt-3">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Application Start:</label>
              <input
                type="text"
                className="form-control"
                name="cp_app_start"
                value={formData.cp_app_start}
                onChange={handleInputChange}
                disabled={isSigned && !isAdmin}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Company Received:</label>
              <input
                type="text"
                className="form-control"
                name="cp_comp_received"
                value={formData.cp_comp_received}
                onChange={handleInputChange}
                disabled={isSigned && !isAdmin}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Certificate Number:</label>
            <input
              type="text"
              className="form-control"
              name="cp_certificate_number"
              value={formData.cp_certificate_number}
              onChange={handleInputChange}
              disabled={isSigned && !isAdmin}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Photo ID Code:</label>
            <input
              type="text"
              className="form-control"
              name="cp_photo_id_code"
              value={formData.cp_photo_id_code}
              onChange={handleInputChange}
              disabled={isSigned && !isAdmin}
            />
          </div>

          <div className="row mb-3">
            {["pending", "yes", "no"].map((key, i) => (
              <div className="col-md-4" key={i}>
                <label className="form-label">{`Photo ID - ${
                  key.charAt(0).toUpperCase() + key.slice(1)
                }`}</label>
                <input
                  type="checkbox"
                  className="form-check-input ms-2"
                  name={`cp_photo_id_${key}`}
                  checked={formData[`cp_photo_id_${key}`]}
                  onChange={handleInputChange}
                  disabled={isSigned && !isAdmin}
                />
              </div>
            ))}
          </div>

          <div className="mb-3">
            <label>Select File (PDF / JPG / PNG) *</label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              disabled={!isAdmin && isSigned}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSigned && !isAdmin}
            >
              Upload
            </button>
          </div>
        </form>

        {uploadedFiles.length > 0 && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Uploaded Files:</div>
            {uploadedFiles.map((file, i) => (
              <div
                key={i}
                className="d-flex justify-content-between align-items-center mb-2"
              >
                <a
                  href={`${API_BASE}/uploads/canadapost/${file.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={file.name}
                >
                  {file.name}
                </a>
                {!isSigned || isAdmin ? (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(file.path)}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        <hr className="my-5" />
        {userId && (
          <>
            <SignatureManager
              taskName="canada_post"
              taskId={4}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="canada_post"
              taskId={4}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default CanadaPost;
